import { Suspense } from 'react';
import { AuthProvider } from 'react-oidc-context';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';

import { RuntimeConfigProvider, type AdminRuntimeConfig } from '../runtime-config';
import { buildOidcProps } from './auth/oidc';
import { RequireAuth } from './auth/RequireAuth';
import { RequireRole } from './auth/RequireRole';
import { moduleRegistry } from './nav/registry';
import { AppLayout } from './shell/AppLayout';
import { EmptyState } from './ui/empty-state';
import { Spinner } from './ui/spinner';

/**
 * The admin shell (admin-shell-oidc-rbac B1). Providers (runtime config + OIDC)
 * wrap an auth-gated router; every module route is role-gated from the registry
 * (B6). `config` is loaded before render (see main.tsx) so the OIDC provider has
 * its authority/client up front.
 */
export function App({ config }: { config: AdminRuntimeConfig }) {
  return (
    <RuntimeConfigProvider value={config}>
      <AuthProvider {...buildOidcProps(config)}>
        <BrowserRouter>
          <Routes>
            <Route
              element={
                <RequireAuth>
                  <AppLayout />
                </RequireAuth>
              }
            >
              <Route index element={<Navigate to="/marketing" replace />} />
              {moduleRegistry.map((module) => {
                const Component = module.component;
                return (
                  <Route
                    key={module.path}
                    path={module.routePattern ?? module.path}
                    element={
                      <RequireRole anyOf={module.requiredRoles}>
                        <Suspense fallback={<Spinner label="Loading…" />}>
                          <Component />
                        </Suspense>
                      </RequireRole>
                    }
                  />
                );
              })}
              {/* L8 round 11 item 4: Banners Logged-out and Outbound Chat were
                  top-level modules (PR-C12) and are now Marketing sections, where
                  Play has them. Redirect the retired BASE paths so bookmarks land
                  somewhere useful; id-bearing deep links are a documented break,
                  the same treatment PR-C12 gave the moved leaderboard list paths. */}
              <Route path="/banners" element={<Navigate to="/marketing/banners" replace />} />
              <Route path="/outbound-chat" element={<Navigate to="/marketing/outbound-chat" replace />} />
              <Route path="*" element={<EmptyState title="Not found" />} />
            </Route>
          </Routes>
        </BrowserRouter>
      </AuthProvider>
    </RuntimeConfigProvider>
  );
}
